/**
 * Eye-code-aware billing catalog (`BACKLOG.md` M10) — the 92002/92004/92012/92014 ophthalmological
 * exam-code family. **UNREVIEWED**, matching `orthopedics/globalSurgicalPeriod.ts`'s own "small curated
 * demo catalog, not a licensed code database" framing: the module brief itself flags this family as
 * "medium-high confidence, not independently re-verified beyond one AAPC page" — treat every entry
 * below as needing independent confirmation against the current CPT manual before real billing use.
 * Special-testing code stacking (visual fields, OCT, fundus photography billing codes) is named in the
 * brief but not catalogued here — deferred, not silently dropped, since none of this pass's sources
 * gave those codes at the same confidence level as the core exam-code family.
 */

export interface OphthalmologyExamCptCode {
  readonly code: string;
  readonly description: string;
  /** `new` = new patient, `established` = established patient — the 92002/92004 vs. 92012/92014 split. */
  readonly patientType: 'new' | 'established';
  /** `intermediate` vs. `comprehensive` — the other axis of this code family. */
  readonly examLevel: 'intermediate' | 'comprehensive';
}

/** UNREVIEWED — see this module's own header. */
export const OPHTHALMOLOGY_EXAM_CPT_CODES: readonly OphthalmologyExamCptCode[] = [
  {
    code: '92002',
    description: 'Ophthalmological exam, new patient, intermediate',
    patientType: 'new',
    examLevel: 'intermediate',
  },
  {
    code: '92004',
    description: 'Ophthalmological exam, new patient, comprehensive',
    patientType: 'new',
    examLevel: 'comprehensive',
  },
  {
    code: '92012',
    description: 'Ophthalmological exam, established patient, intermediate',
    patientType: 'established',
    examLevel: 'intermediate',
  },
  {
    code: '92014',
    description: 'Ophthalmological exam, established patient, comprehensive',
    patientType: 'established',
    examLevel: 'comprehensive',
  },
] as const;

export const OPHTHALMOLOGY_EXAM_CPT_CODE_VALUES = OPHTHALMOLOGY_EXAM_CPT_CODES.map(
  (c) => c.code,
) as [string, ...string[]];

export function findOphthalmologyExamCptCode(code: string): OphthalmologyExamCptCode | undefined {
  return OPHTHALMOLOGY_EXAM_CPT_CODES.find((entry) => entry.code === code);
}
